/**
 * The cash-refund rules, as the Refund Manager needs them.
 *
 * These MIRROR ronin-api/api/cashRefund/_helpers.js — refundSettings,
 * payoutConfigured, eventEndedAt, refundsOpenAt, MIN_PAYOUT_AMOUNT. They are
 * copied rather than shared because the two apps do not share code, and the
 * cost of that is drift: if the API's rules change, change these in the same
 * commit, or this page will tell ops a refund is open when the API refuses it.
 *
 * Money is integer cents throughout, per the repo rule.
 */

/** Smallest amount worth paying, in cents, after Ronin's fee. Mirrors the API. */
export const MIN_PAYOUT_CENTS = 100;

/** Same table as _helpers.js and EventEdit.tsx. */
const TIMEZONE_OFFSETS: Record<string, number> = {
  ADT: -3, AST: -4, EDT: -4, EST: -5, CDT: -5,
  CST: -6, MDT: -6, MST: -7, PDT: -7, PST: -8,
};

/** Per-event settings for the payment file. Mirrors _helpers.js. */
export interface AchConfig {
  /** CCD (default) or PPD. See cash-refunds-setup.md section 3. */
  sec_code?: string | null;
  /** Up to 10 characters, and what the attendee sees on their statement. */
  entry_description?: string | null;
  /** Banking days between building the file and the money arriving. */
  effective_days?: number | null;
}

/**
 * What is stored at events.cashless_configurations.cash_refund. Every key is
 * optional because older or hand-edited configs may carry any subset, and any
 * key this page does not edit (min_payout among them) must survive a save
 * untouched — hence the index signature.
 *
 * The Chase account and the ledger user id are NOT here. They are the same for
 * every event, so they live in the API environment as CASH_REFUND_ACH_ACCOUNT
 * and CASH_REFUND_UPDATED_BY. Older configs may still carry account_number or
 * updated_by; the API ignores both.
 */
/** The organizer's own wording for the attendee-facing refund page. */
export interface CashRefundCopy {
  heading?: string | null;
  intro?: string | null;
  step_one?: string | null;
  step_two?: string | null;
  step_three?: string | null;
  uid_label?: string | null;
  uid_placeholder?: string | null;
}

export interface CashRefundConfig {
  enabled?: boolean;
  /** Blank or missing means the refund page uses its own default wording. */
  copy?: CashRefundCopy | null;
  ach?: AchConfig;
  fee_amount?: number;
  min_amount?: number;
  min_payout?: number;
  [key: string]: unknown;
}

export interface RefundEvent {
  id: number;
  name: string;
  slug: string | null;
  end_date: string | null;
  timezone: string | null;
  timezone_diff: number | null;
  is_active: boolean | null;
  cashless_configurations: Record<string, unknown> | null;
}

export const configOf = (event: RefundEvent | null | undefined): CashRefundConfig | null => {
  const raw = event?.cashless_configurations?.cash_refund;
  return raw && typeof raw === "object" ? (raw as CashRefundConfig) : null;
};

const num = (value: unknown, fallback: number) =>
  Number.isFinite(Number(value)) ? Number(value) : fallback;

/** The API's defaults, applied the same way. */
export const effective = (config: CashRefundConfig | null) => ({
  enabled: config?.enabled === true,
  ach: {
    secCode: config?.ach?.sec_code === "PPD" ? "PPD" : "CCD",
    entryDescription: String(config?.ach?.entry_description || "REFUND")
      .toUpperCase()
      .slice(0, 10),
    effectiveDays: Math.min(30, Math.max(1, num(config?.ach?.effective_days, 1))),
  },
  feeAmount: num(config?.fee_amount, 0),
  minAmount: num(config?.min_amount, 100),
  minPayout: Math.max(1, num(config?.min_payout, MIN_PAYOUT_CENTS)),
});

export type Effective = ReturnType<typeof effective>;

/**
 * What this page can actually know about readiness — which is less than it
 * used to be.
 *
 * The API's payoutConfigured() also requires CASH_REFUND_ACH_ACCOUNT,
 * CASH_REFUND_UPDATED_BY and CASH_REFUND_BANK_KEY, none of which a browser can
 * see. So an event that looks ready here can still be refused server-side. The
 * ACH Payouts tab is the honest answer: its `problems` list comes from the API
 * and names whichever of those is missing.
 */
export const payoutConfigured = (e: Effective) => e.enabled;

/** End of end_date in the event's own timezone. Ignores free-text end_time, as the API does. */
export function eventEndedAt(event: RefundEvent): Date | null {
  const raw = event.end_date;
  if (!raw) return null;
  const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const stored = Number(event.timezone_diff);
  const abbreviation = (event.timezone || "").toUpperCase().trim();
  const offset =
    Number.isFinite(stored) && stored !== 0 ? stored : TIMEZONE_OFFSETS[abbreviation] ?? 0;
  const endOfDayUtc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + 1);
  return new Date(endOfDayUtc - offset * 3600 * 1000);
}

/** Refunds open when the event has ended, and stay open. Mirrors the API. */
export function refundsOpenAt(event: RefundEvent): Date | null {
  return eventEndedAt(event);
}

export type RefundState =
  | "not_configured"
  | "off"
  | "no_end_date"
  | "not_open_yet"
  | "open";

/** The single answer to "can an attendee get a refund on this event right now?" */
export function stateOf(event: RefundEvent, now = new Date()): RefundState {
  const config = configOf(event);
  if (!config) return "not_configured";
  const e = effective(config);
  if (!e.enabled) return "off";
  const opens = refundsOpenAt(event);
  if (!opens) return "no_end_date";
  if (now < opens) return "not_open_yet";
  // No "closed": refunds stay open once an event has ended. And no
  // "incomplete" — the account, ledger user and encryption key are all in the
  // API environment now, so only the ACH Payouts tab can report on them.
  return "open";
}

export const STATE_LABEL: Record<RefundState, string> = {
  not_configured: "Not set up",
  off: "Off",
  no_end_date: "No end date",
  not_open_yet: "Not open yet",
  open: "Open",
};

/** Smallest wristband balance that can actually be refunded under these settings. */
export const smallestRefundable = (e: Effective) =>
  Math.max(e.minAmount, e.feeAmount + e.minPayout);

/** What an attendee receives from a band, before any fee for the method they pick. */
export const netFor = (balanceCents: number, e: Effective) =>
  balanceCents >= smallestRefundable(e) ? balanceCents - e.feeAmount : 0;

export const dollarsToCents = (value: string): number | null => {
  const trimmed = String(value ?? "").replace(/[$,\s]/g, "");
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
};

export const centsToDollars = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? "" : (cents / 100).toFixed(2);

export const money = (cents: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    (cents || 0) / 100
  );

/**
 * Order-independent equality for JSON values. Postgres jsonb stores object keys
 * in its own order (shortest first, then alphabetical), so what comes back from
 * a save never has the keys in the order they were sent — a plain
 * JSON.stringify comparison would call every successful save a mismatch.
 */
/**
 * What blocks a save of the cash-refund settings, wherever they are edited.
 *
 * Lives here rather than in the panel because the Event page edits the same
 * config: two copies of Chase's PPD/PAYROLL rule is two chances to get it
 * wrong, and a rejected ACH file is found out days later.
 */
export function cashRefundErrors(input: {
  feeCents: number | null;
  minimumCents: number | null;
  secCode: string;
  entryDescription: string;
}): string[] {
  const errors: string[] = [];
  if (input.feeCents === null) errors.push("Ronin fee must be a dollar amount (0 for none).");
  if (input.minimumCents === null) errors.push("Minimum refund must be a dollar amount.");

  // Chase's own rule: PPD batches must say PAYROLL, and CCD batches must not.
  const description = String(input.entryDescription || "").toUpperCase();
  if (input.secCode === "PPD" && description !== "PAYROLL")
    errors.push('Chase only accepts PPD batches with the description "PAYROLL".');
  if (input.secCode === "CCD" && description === "PAYROLL")
    errors.push('"PAYROLL" cannot be the description on a CCD batch.');

  return errors;
}

export function sameJson(a: unknown, b: unknown): boolean {
  const canon = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(canon);
    if (v && typeof v === "object") {
      return Object.keys(v as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, k) => {
          const value = (v as Record<string, unknown>)[k];
          // null and absent mean the same thing in this config (closes_at: null
          // is "no close date"), so neither makes a config look changed.
          if (value !== undefined && value !== null) acc[k] = canon(value);
          return acc;
        }, {});
    }
    return v;
  };
  return JSON.stringify(canon(a)) === JSON.stringify(canon(b));
}
